import _ from 'lodash';
import _isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';
import update from 'immutability-helper';
import React, { Component, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { reverse } from 'named-urls';
import routes from '@achievable-me/tundra-wizard/routes';
import {
  hasFullAccess,
  isAdmin,
  isSales,
  isStaff,
} from '@achievable-me/tundra-wizard/auth';
import partners from '@achievable-me/tundra-wizard/partners';
import MenuItem from '$components/layout/menu/MenuItem';
import logoSrc from '$assets/logoWhite.png';
import styles from './styles.scss';
import mountainSrc from './shapeMountain.png';
import { checkProductSlug } from '$lib/checkProductSlug';

const ACHIEVABLE_INTRO_CONTENT_UUID = 'the-achievable-method';
const PRODUCTS_WITH_RESOURCES = ['finra-sie', 'finra-series-7'];

const ORG_UUID_MY_GURU = '2941112c-5e31-41c1-bffa-fafa9beb6650';

class ContentMenu extends Component {
  renderRecursiveContentMenuItem = ({ isAchievable, contentInstance, level }) => {
    const { selectedKeys, openKeys, handleMenuItemClick } = this.props;

    const hasChildren = !_isEmpty(contentInstance.content);

    const isOpen = isAchievable || openKeys[contentInstance.key];

    const caret = (() => {
      if (!hasChildren) {
        return null;
      }

      return isOpen ? 'up' : 'down';
    })();

    return (
      <Fragment key={`fragment-${contentInstance.key}`}>
        <MenuItem
          data-aaa={`contentNav-${contentInstance.key}`}
          selected={selectedKeys[contentInstance.key]}
          selectable={!hasChildren}
          key={contentInstance.key}
          menuKey={contentInstance.key}
          caret={caret}
          text={contentInstance.title}
          level={level}
          to={contentInstance.path}
          handleMenuItemClick={handleMenuItemClick}
        />
        {isOpen &&
          contentInstance.content.map((child) =>
            this.renderRecursiveContentMenuItem({
              isAchievable,
              contentInstance: child,
              level: level + 1,
            })
          )}
      </Fragment>
    );
  };

  render() {
    const { isAchievable, openKeys, inflatedContent, handleMenuItemClick, product } =
      this.props;
    const { slug: productSlug } = product;
    const text = isAchievable ? 'How it works' : 'Textbook';

    return (
      <>
        <MenuItem
          data-aaa="contentNav"
          data-product-slug={productSlug}
          key="content"
          menuKey="content"
          iconClass="fad fa-books"
          text={text}
          caret={openKeys.content ? 'up' : 'down'}
          selectable={false}
          handleMenuItemClick={handleMenuItemClick}
        />
        {openKeys.content &&
          inflatedContent.map((contentInstance) =>
            this.renderRecursiveContentMenuItem({
              isAchievable,
              contentInstance,
              level: 1,
            })
          )}
      </>
    );
  }
}

class DesktopSidenav extends Component {
  constructor(props) {
    super(props);

    const { defaultOpenKeys = [], defaultSelectedKeys = [] } = props;

    const openKeys = defaultOpenKeys.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});

    const selectedKeys = defaultSelectedKeys.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});

    this.state = {
      openKeys,
      selectedKeys,
    };
  }

  handleMenuItemClick = ({ menuKey, selectable }) => {
    const { openKeys: previousOpenKeys } = this.state;
    const { toggleSidebarExpanded } = this.props;

    const openKeys = update(previousOpenKeys, {
      [menuKey]: {
        $set: !previousOpenKeys[menuKey],
      },
    });

    this.setState({
      openKeys,
    });

    if (!selectable) {
      return;
    }

    const selectedKeys = {
      [menuKey]: true,
    };

    this.setState({
      selectedKeys,
    });

    toggleSidebarExpanded();
  };

  renderUserMenu() {
    const { currentUser, inflatedContent, product } = this.props;
    const { openKeys, selectedKeys } = this.state;
    const { slug: productSlug } = product;
    const { isAchievable, showPracticeExams } = checkProductSlug(productSlug);

    const showPurchase = !isAchievable;
    const needsToBuy = !hasFullAccess({ user: currentUser, product });

    const showTutoring =
      !isAchievable && _.get(currentUser, 'organizationUuid') !== ORG_UUID_MY_GURU;

    return (
      <>
        {!currentUser && (
          <MenuItem
            to={routes.auth.signIn}
            data-aaa="signInNav"
            data-product-slug={productSlug}
            iconClass="fad fa-sign-in"
            text="Sign in"
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {!currentUser && (
          <MenuItem
            to={routes.auth.signUp}
            data-aaa="signUpNav"
            data-product-slug={productSlug}
            iconClass="fad fa-user-plus"
            text="Sign up"
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {!isAchievable && currentUser && (
          <MenuItem
            data-aaa="dashboardNav"
            data-product-slug={productSlug}
            selected={selectedKeys.dashboard}
            to={reverse(routes.study.dashboard, { productSlug })}
            iconClass="fad fa-chart-line"
            text="Dashboard"
            onClick={() => this.handleMenuItemClick({ menuKey: 'dashboard' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {showPurchase && needsToBuy && (
          <MenuItem
            data-aaa="purchaseNav"
            data-product-slug={productSlug}
            selected={selectedKeys.purchase}
            to={reverse(routes.study.purchase, { productSlug })}
            iconClass="fad fa-file-invoice-dollar"
            text="Purchase"
            onClick={() => this.handleMenuItemClick({ menuKey: 'purchase' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        <ContentMenu
          isAchievable={isAchievable}
          openKeys={openKeys}
          selectedKeys={selectedKeys}
          inflatedContent={inflatedContent}
          handleMenuItemClick={this.handleMenuItemClick}
          product={product}
        />
        {showPracticeExams && (
          <MenuItem
            data-aaa="examsNav"
            data-product-slug={productSlug}
            selected={selectedKeys.exams}
            to={reverse(routes.study.exams, { productSlug })}
            iconClass="fad fa-tachometer-alt"
            text="Practice exams"
            onClick={() => this.handleMenuItemClick({ menuKey: 'exams' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {!isAchievable && (
          <MenuItem
            data-aaa="overviewNav"
            data-product-slug={productSlug}
            selected={selectedKeys.overview}
            to={reverse(routes.study.overview, { productSlug })}
            iconClass="fad fa-file-certificate"
            text="Exam overview"
            onClick={() => this.handleMenuItemClick({ menuKey: 'overview' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        <MenuItem
          data-aaa="feedbackNav"
          data-product-slug={productSlug}
          selected={selectedKeys.feedback}
          to={reverse(routes.study.feedback, { productSlug })}
          iconClass="fad fa-envelope"
          text="Feedback"
          onClick={() => this.handleMenuItemClick({ menuKey: 'feedback' })}
          handleMenuItemClick={this.handleMenuItemClick}
        />
        <MenuItem
          data-aaa="communityNav"
          data-product-slug={productSlug}
          selected={selectedKeys.community}
          href="https://talk.achievable.me"
          iconClass="fad fa-comments"
          text="Community"
          onClick={() => this.handleMenuItemClick({ menuKey: 'community' })}
          handleMenuItemClick={this.handleMenuItemClick}
        />
        {!isAchievable && (
          <MenuItem
            data-aaa="howItWorksNav"
            data-product-slug={productSlug}
            selected={selectedKeys.howItWorks}
            to={reverse(routes.study.learn, {
              productSlug: 'achievable',
              contentUuid: ACHIEVABLE_INTRO_CONTENT_UUID,
            })}
            iconClass="fad fa-life-ring"
            text="How it works"
            onClick={() => this.handleMenuItemClick({ menuKey: 'howItWorks' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {showTutoring && (
          <MenuItem
            data-aaa="tutoringNav"
            data-product-slug={productSlug}
            selected={selectedKeys.tutoring}
            to={reverse(routes.study.tutoring, { productSlug })}
            iconClass="fad fa-chalkboard-teacher"
            text="Tutoring"
            onClick={() => this.handleMenuItemClick({ menuKey: 'tutoring' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {!isAchievable && _.includes(PRODUCTS_WITH_RESOURCES, productSlug) && (
          <MenuItem
            data-aaa="resourcesNav"
            data-product-slug={productSlug}
            selected={selectedKeys.resources}
            to={reverse(routes.study.resources, { productSlug })}
            iconClass="fad fa-bookmark"
            text="Resources"
            onClick={() => this.handleMenuItemClick({ menuKey: 'resources' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        <MenuItem
          data-aaa="catalogNav"
          data-product-slug={productSlug}
          selected={selectedKeys.catalog}
          to={reverse(routes.study.catalog, { productSlug: 'achievable' })}
          iconClass="fad fa-layer-group"
          text="Other courses"
          onClick={() => this.handleMenuItemClick({ menuKey: 'catalog' })}
          handleMenuItemClick={this.handleMenuItemClick}
        />
        {currentUser && (
          <MenuItem
            data-aaa="accountNav"
            data-product-slug={productSlug}
            selected={selectedKeys.account}
            to={reverse(routes.study.account, { productSlug })}
            iconClass="fad fa-user-circle"
            text="User account"
            onClick={() => this.handleMenuItemClick({ menuKey: 'account' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {isStaff(currentUser) && (
          <MenuItem
            to={reverse(routes.staff.dashboard)}
            iconClass="fad fa-tools"
            text="Manage"
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {currentUser && (
          <MenuItem
            to={routes.auth.signOut}
            data-aaa="signOutNav"
            data-product-slug={productSlug}
            iconClass="fad fa-sign-out-alt"
            text="Sign out"
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
      </>
    );
  }

  renderStaffMenu() {
    const { currentUser, location } = this.props;
    const { selectedKeys } = this.state;
    const partnerSlug = partners.permissions[currentUser.email.toLowerCase()];

    const { pathname } = location;
    const match = /products\/(.*?)\//.exec(pathname);
    const productSlug = match ? match[1] : null;

    return (
      <>
        <MenuItem
          data-aaa="staffDashboardNav"
          data-product-slug={productSlug}
          selected={selectedKeys.staffDashboard}
          to={reverse(routes.staff.dashboard)}
          iconClass="fad fa-analytics"
          text="Dashboard"
          onClick={() => this.handleMenuItemClick({ menuKey: 'staffDashboard' })}
          handleMenuItemClick={this.handleMenuItemClick}
        />
        {partnerSlug && (
          <MenuItem
            data-aaa="staffChargesNav"
            selected={selectedKeys.staffCharges}
            to={reverse(routes.staff.charges, { partnerSlug })}
            iconClass="fad fa-sack-dollar"
            text="Charges"
            onClick={() => this.handleMenuItemClick({ menuKey: 'staffCharges' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {(isAdmin(currentUser) || isSales(currentUser)) && (
          <MenuItem
            data-aaa="staffOrganizationsNav"
            selected={selectedKeys.staffOrganizations}
            to={routes.staff.organizations}
            iconClass="fad fa-sitemap"
            text="Organizations"
            onClick={() => this.handleMenuItemClick({ menuKey: 'staffOrganizations' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {isAdmin(currentUser) && (
          <MenuItem
            data-aaa="staffProductsNav"
            selected={selectedKeys.staffProducts}
            to={routes.staff.products}
            iconClass="fad fa-th-list"
            text="Products"
            onClick={() => this.handleMenuItemClick({ menuKey: 'staffProducts' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {productSlug && (
          <div className={styles.staffProductMenu}>
            <MenuItem
              data-aaa="staffProductOverviewNav"
              selected={selectedKeys.staffProductOverview}
              to={reverse(routes.staff.product.overview, { productSlug })}
              iconClass="fad fa-user-chart"
              text="Overview"
              onClick={() =>
                this.handleMenuItemClick({ menuKey: 'staffProductOverview' })
              }
              handleMenuItemClick={this.handleMenuItemClick}
            />
            <MenuItem
              data-aaa="staffProductContentNav"
              selected={selectedKeys.staffProductContent}
              to={reverse(routes.staff.product.content.overview, { productSlug })}
              iconClass="fad fa-book"
              text="Content"
              onClick={() => this.handleMenuItemClick({ menuKey: 'staffProductContent' })}
              handleMenuItemClick={this.handleMenuItemClick}
            />
            <MenuItem
              data-aaa="staffProductFlashcardsNav"
              selected={selectedKeys.staffProductFlashcards}
              to={reverse(routes.staff.product.flashcards, { productSlug })}
              iconClass="fad fa-sticky-note"
              text="Quizzes"
              onClick={() =>
                this.handleMenuItemClick({ menuKey: 'staffProductFlashcards' })
              }
              handleMenuItemClick={this.handleMenuItemClick}
            />
          </div>
        )}
        {(isAdmin(currentUser) || isSales(currentUser)) && (
          <MenuItem
            data-aaa="staffUsersNav"
            selected={selectedKeys.staffUsers}
            to={routes.staff.users}
            iconClass="fad fa-users"
            text="Users"
            onClick={() => this.handleMenuItemClick({ menuKey: 'staffUsers' })}
            handleMenuItemClick={this.handleMenuItemClick}
          />
        )}
        {isAdmin(currentUser) && (
          <>
            <MenuItem
              data-aaa="staffPromoCodesNav"
              selected={selectedKeys.staffPromoCodes}
              to={routes.staff.promoCodes}
              iconClass="fad fa-tags"
              text="Promo codes"
              onClick={() => this.handleMenuItemClick({ menuKey: 'staffPromoCodes' })}
              handleMenuItemClick={this.handleMenuItemClick}
            />
            <MenuItem
              data-aaa="staffGrantsNav"
              selected={selectedKeys.staffGrants}
              to={routes.staff.grants}
              iconClass="fad fa-shield-check"
              text="Grants"
              onClick={() => this.handleMenuItemClick({ menuKey: 'staffGrants' })}
              handleMenuItemClick={this.handleMenuItemClick}
            />
            <MenuItem
              data-aaa="staffSystemNav"
              selected={selectedKeys.staffSystem}
              to={routes.staff.system}
              iconClass="fad fa-database"
              text="System"
              onClick={() => this.handleMenuItemClick({ menuKey: 'staffSystem' })}
              handleMenuItemClick={this.handleMenuItemClick}
            />
          </>
        )}
        <MenuItem
          data-aaa="catalogNav"
          selected={selectedKeys.catalog}
          to={reverse(routes.study.catalog, { productSlug: 'achievable' })}
          iconClass="fad fa-layer-group"
          text="Course catalog"
          onClick={() => this.handleMenuItemClick({ menuKey: 'catalog' })}
          handleMenuItemClick={this.handleMenuItemClick}
        />
        <MenuItem
          to={routes.auth.signOut}
          data-aaa="signOutNav"
          iconClass="fad fa-sign-out-alt"
          text="Sign out"
          handleMenuItemClick={this.handleMenuItemClick}
        />
      </>
    );
  }

  render() {
    const { sidebarExpanded, toggleSidebarExpanded, mode } = this.props;

    const layoutClass = classnames([styles.layout, !sidebarExpanded && styles.hidden]);

    return (
      <div className={layoutClass}>
        <div
          data-aaa="navBackdrop"
          className={styles.backdrop}
          onClick={toggleSidebarExpanded}
        />
        <div data-aaa="sidenavLayoutInner" className={styles.inner}>
          <div
            data-aaa="navClose"
            className={styles.close}
            onClick={toggleSidebarExpanded}
          >
            <i className="fas fa-times" />
          </div>
          <Link to="/" data-aaa="logoNav">
            <div className={styles.logoSection}>
              <img src={logoSrc} className={styles.logoImg} />
              <div
                className={classnames(styles.logoText, mode === 'staff' && styles.staff)}
              >
                Achievable
              </div>
            </div>
          </Link>
          <div className={styles.menu}>
            {mode === 'staff' ? this.renderStaffMenu() : this.renderUserMenu()}
          </div>
          <div className={styles.mountain}>
            <img src={mountainSrc} className={styles.mountainImg} />
          </div>
        </div>
      </div>
    );
  }
}

export default DesktopSidenav;
